import React, {useEffect, useRef} from "react";
import "./UiCarousel.css";

export type UiCarouselImageAndTextType = {
    imageUrl: string,
    title: string,
    text?: string,
    altImage?: string,
}

export type UiCarouselType = {
    className?: string,
    imagesAndTexts: UiCarouselImageAndTextType[],
    duration?: number,
}

export const UiCarousel: React.FC<UiCarouselType> = ({
    className,
    imagesAndTexts,
    duration = 5
}) => {
    const [activeImageAndText, setActiveImageAndText] = React.useState<UiCarouselImageAndTextType>(imagesAndTexts[0]);
    const intervalRef = useRef<NodeJS.Timeout | null>(null);

    const startTimer = () => {
        if (intervalRef.current) {
            clearInterval(intervalRef.current);
        }
        
        intervalRef.current = setInterval(() => {
            setActiveImageAndText((prevUrl) => {
                const currentIndex = imagesAndTexts.indexOf(prevUrl);
                const nextIndex = (currentIndex + 1) % imagesAndTexts.length;
                return imagesAndTexts[nextIndex];
            });
        }, duration * 1000);
    };

    useEffect(() => {
        startTimer();
        
        return () => {
            if (intervalRef.current) {
                clearInterval(intervalRef.current);
            }
        };
    }, []);

    const handleImageChange = (imageAndText: UiCarouselImageAndTextType) => {
        setActiveImageAndText(imageAndText);
        startTimer(); // Reset the timer
    };

    return (
        <div className={(className ? `${className} ` : "") + "w-full"}>
            {/* content */}
            <div className={"flex flex-col items-center px-10"}>
                <img
                    className={"w-11/12 h-auto border-purple-blue border-4 rounded-xl"}
                    src={activeImageAndText.imageUrl}
                    alt={activeImageAndText.altImage ? activeImageAndText.altImage : ""}
                />

                <div className="mt-16 h-40">
                    <p className="text-3xl text-center">{activeImageAndText.title}</p>
                    <p className="mt-4 text-gray-200 h-24 text-center">{activeImageAndText.text}</p>
                </div>
            </div>

            {/* Page selector */}
            <div className="flex gap-5 justify-center w-full">
                {imagesAndTexts.map((imageAndText) => (
                    <div
                        key={imageAndText.imageUrl}
                        className={"h-4 w-32 rounded-xl bg-deep-blue cursor-pointer " +
                            (imageAndText === activeImageAndText ?
                                "" : "hover:bg-deep-blue-hover")}
                        onClick={() => handleImageChange(imageAndText)}
                    >
                        {imageAndText === activeImageAndText &&
                            <div
                                className="bg-purple-blue rounded-full min-w-2.5 h-4"
                                style={{
                                    animation: `fill ${duration}s linear`
                                }}
                        />}
                    </div>
                ))}
            </div>
        </div>
    );
};
